'use client';

// Lightweight GA4 event layer. All site analytics goes through trackEvent so call
// sites never touch window.gtag directly and stay safe when GA is absent (SSR,
// blocked, or no measurement id).

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type EventParams = Record<string, unknown>;

export function trackEvent(name: string, params: EventParams = {}): void {
  if (typeof window === 'undefined') return;
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return;
  gtag('event', name, params);
}

const clamp = (value: string, max = 300) => (value.length > max ? value.slice(0, max) : value);

// One delegated click listener for the whole document covers outbound links,
// app-store CTAs, and tool result downloads — no per-component instrumentation.
export function handleDelegatedClick(event: MouseEvent): void {
  const anchor = (event.target as HTMLElement | null)?.closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#')) return;

  const pagePath = window.location.pathname;

  // Tool result downloads use real <a download> elements.
  if (anchor.hasAttribute('download')) {
    const fileName = anchor.getAttribute('download') || href.split('/').pop() || 'file';
    trackEvent('file_download', { file_name: clamp(fileName, 120), page_path: pagePath });
    return;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (!/^https?:$/.test(url.protocol)) return;
  if (url.origin === window.location.origin) return;

  const host = url.hostname.replace(/^www\./, '');
  const isAppStore =
    host.includes('apps.apple.com') ||
    host.includes('itunes.apple.com') ||
    host.includes('play.google.com');

  if (isAppStore) {
    const store = host.includes('apple') ? 'app_store' : 'google_play';
    // Primary conversion: monetization lives in the app, so app-store clicks matter most.
    trackEvent('app_store_click', {
      store,
      link_url: clamp(url.href),
      page_path: pagePath,
    });
  }

  trackEvent('click', {
    outbound: true,
    link_url: clamp(url.href),
    link_domain: host,
    page_path: pagePath,
  });
}

// Scroll-depth milestones per page (reset on navigation by the caller).
export function makeScrollDepthTracker() {
  const milestones = [25, 50, 75, 90];
  let fired = new Set<number>();

  const onScroll = () => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
    for (const m of milestones) {
      if (percent >= m && !fired.has(m)) {
        fired.add(m);
        trackEvent('scroll_depth', { percent_scrolled: m, page_path: window.location.pathname });
      }
    }
  };

  return {
    onScroll,
    reset: () => {
      fired = new Set<number>();
    },
  };
}
